import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { PageHeader } from '../../components/molecules';

interface Appointment {
  id: string;
  patientName: string;
  ownerName: string;
  date: string;
  time: string;
  type: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes: string;
}

const statusStyles: Record<
  Appointment['status'],
  { badge: string; label: string; accent: string }
> = {
  scheduled: {
    badge: 'bg-amber-100 text-amber-600 border border-amber-200',
    label: 'Scheduled',
    accent: 'text-amber-500'
  },
  completed: {
    badge: 'bg-emerald-100 text-emerald-600 border border-emerald-200',
    label: 'Completed',
    accent: 'text-emerald-500'
  },
  cancelled: {
    badge: 'bg-rose-100 text-rose-600 border border-rose-200',
    label: 'Cancelled',
    accent: 'text-rose-500'
  }
};

const appointmentTypes = [
  'Regular Checkup',
  'Vaccination',
  'Surgery',
  'Emergency',
  'Grooming',
  'Dental Care',
  'Consultation'
];

const Appointments: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([
    {
      id: '1',
      patientName: 'Max',
      ownerName: 'John Doe',
      date: '2024-01-15',
      time: '09:00',
      type: 'Regular Checkup',
      status: 'scheduled',
      notes: 'Annual vaccination'
    },
    {
      id: '2',
      patientName: 'Luna',
      ownerName: 'Jane Smith',
      date: '2024-01-15',
      time: '10:30',
      type: 'Surgery',
      status: 'scheduled',
      notes: 'Dental cleaning'
    }
  ]);

  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Appointment['status']>('all');

  const [formData, setFormData] = useState({
    patientName: '',
    ownerName: '',
    date: '',
    time: '',
    type: 'Regular Checkup',
    notes: ''
  });

  const filteredAppointments = useMemo(() => {
    return appointments.filter((appointment) => {
      const matchesSearch =
        appointment.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        appointment.ownerName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || appointment.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [appointments, searchTerm, statusFilter]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newAppointment: Appointment = {
        id: Date.now().toString(),
        ...formData,
        status: 'scheduled'
      };

      setAppointments((prev) => [...prev, newAppointment]);
      setFormData({
        patientName: '',
        ownerName: '',
        date: '',
        time: '',
        type: 'Regular Checkup',
        notes: ''
      });
      setShowForm(false);
      toast.success(`Appointment for ${newAppointment.patientName} scheduled successfully.`);
    } catch (err) {
      toast.error('Unable to create appointment. Please try again.');
    }
  };

  const handleStatusChange = (id: string, newStatus: Appointment['status']) => {
    try {
      const appointment = appointments.find((apt) => apt.id === id);
      setAppointments((prev) =>
        prev.map((apt) => (apt.id === id ? { ...apt, status: newStatus } : apt))
      );
      if (appointment) {
        const statusText =
          newStatus === 'scheduled'
            ? 'scheduled'
            : newStatus === 'completed'
            ? 'completed'
            : 'cancelled';
        toast.success(`Appointment for ${appointment.patientName} marked as ${statusText}.`);
      }
    } catch (err) {
      toast.error('Failed to update appointment status. Please try again.');
    }
  };

  const handleDelete = (id: string) => {
    try {
      const appointment = appointments.find((apt) => apt.id === id);
      setAppointments((prev) => prev.filter((apt) => apt.id !== id));
      if (appointment) {
        toast.success(`Appointment for ${appointment.patientName} has been removed.`);
      }
    } catch (err) {
      toast.error('Failed to delete appointment. Please try again.');
    }
  };

  const scheduledCount = appointments.filter((appointment) => appointment.status === 'scheduled').length;
  const completedCount = appointments.filter((appointment) => appointment.status === 'completed').length;
  const cancelledCount = appointments.filter((appointment) => appointment.status === 'cancelled').length;
  const filtersActive = Boolean(searchTerm) || statusFilter !== 'all';
  const filterSummary = filtersActive
    ? [
        searchTerm ? `Keyword: "${searchTerm}"` : null,
        statusFilter !== 'all' ? `Status: ${statusStyles[statusFilter]?.label || statusFilter}` : null
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Showing every appointment';

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Schedule"
        title="Appointments"
        description="Coordinate visits, prep rooms, and keep everyone aligned from a single view."
        icon="🗓️"
        accent="amber"
        actions={(
          <button
            className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
            onClick={() => setShowForm(true)}
          >
            <span className="text-lg leading-none">➕</span>
            Schedule appointment
          </button>
        )}
        stats={[
          {
            label: 'Scheduled',
            value: scheduledCount,
            helper: 'Awaiting visits'
          },
          {
            label: 'Completed',
            value: completedCount,
            helper: 'Done this week'
          },
          {
            label: 'Cancelled',
            value: cancelledCount,
            helper: 'Need follow-up'
          }
        ]}
      >
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
          <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
            Search appointments
            <input
              type="text"
              placeholder="Search by pet or owner name…"
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
            Status
            <select
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="all">All statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
        </div>
        <p className="mt-3 text-xs text-slate-500">{filterSummary}</p>
      </PageHeader>

      <section className="space-y-4">
        {filteredAppointments.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-brand-muted bg-white/70 px-8 py-16 text-center shadow-soft">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/10 text-3xl text-brand-primary">
              📭
            </div>
            <h3 className="mt-6 text-lg font-semibold text-slate-700">
              No appointments match your filters
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Try adjusting your search terms or schedule a new appointment to fill your calendar.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {filteredAppointments.map((appointment) => {
              const status = statusStyles[appointment.status];
              return (
                <article
                  key={appointment.id}
                  className="flex h-full flex-col justify-between rounded-3xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <header className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-primary/10 text-lg text-brand-primary">
                          {appointment.patientName.charAt(0)}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-800">
                            {appointment.patientName}
                          </h3>
                          <p className="text-sm text-slate-500">Owner: {appointment.ownerName}</p>
                        </div>
                      </div>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${status.badge}`}>
                      {status.label}
                    </span>
                  </header>

                  <dl className="mt-6 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Date
                      </dt>
                      <dd className="mt-2 text-slate-700">
                        {new Date(appointment.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Time
                      </dt>
                      <dd className="mt-2 text-slate-700">{appointment.time}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Notes
                      </dt>
                      <dd className="mt-2 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-slate-600">
                        {appointment.notes || 'No additional notes provided.'}
                      </dd>
                    </div>
                  </dl>

                  <footer className="mt-6 flex flex-wrap items-center gap-2">
                    <button
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-600"
                      onClick={() => handleStatusChange(appointment.id, 'completed')}
                    >
                      ✅ Mark completed
                    </button>
                    <button
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600"
                      onClick={() => handleStatusChange(appointment.id, 'scheduled')}
                    >
                      🔁 Reschedule
                    </button>
                    <button
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
                      onClick={() => handleStatusChange(appointment.id, 'cancelled')}
                    >
                      🛑 Cancel
                    </button>
                    <button
                      className="ml-auto inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-100"
                      onClick={() => handleDelete(appointment.id)}
                    >
                      🗑 Delete
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 shadow-soft">
            <button
              className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-lg text-slate-500 transition hover:bg-slate-100"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
            >
              ✕
            </button>

            <header>
              <h2 className="text-xl font-semibold text-slate-800">Schedule an appointment</h2>
              <p className="mt-2 text-sm text-slate-500">
                Capture visit details so your team can prepare ahead of the appointment.
              </p>
            </header>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                  Patient name
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.patientName}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, patientName: e.target.value }))
                    }
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                  Owner name
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.ownerName}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, ownerName: e.target.value }))
                    }
                    required
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                  Date
                  <input
                    type="date"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                  Time
                  <input
                    type="time"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.time}
                    onChange={(e) => setFormData((prev) => ({ ...prev, time: e.target.value }))}
                    required
                  />
                </label>
              </div>

              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                Appointment type
                <select
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  value={formData.type}
                  onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
                >
                  {appointmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                Notes
                <textarea
                  rows={4}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add special instructions, pre-visit notes, or treatment reminders."
                />
              </label>

              <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-full bg-brand-primary px-6 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
                >
                  Save appointment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
